import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { SHAPE_COMPONENT, SHAPE_FACTSHEET_BLUEPRINT } from '../../shapes'
import { withTranslation } from 'react-i18next'
import { getAttributeRequired, getComponentDefinition, getComponentLabel, getTrackingFactLabel } from '../../helper'
import DatePicker from '../../../DatePicker'
import dayjs from 'dayjs'
import './styles.scss'
import activityTrackingService from '../../../../../services/activityTrackingService'
import { trackingEvent } from '../../../../../constants'
import FactsheetComponentLabel from '../factsheet-component-label'

class AttributeDateRangeSelect extends PureComponent {

  dateFormat = 'YYYY-MM-DD'
  state = {
    startDate: null,
    endDate: null,
    valid: true,
    required: false
  }

  componentDidMount () {
    this.updateState()
  }

  componentDidUpdate (prevProps) {
    if (
      prevProps.data !== this.props.data
      || prevProps.blueprint !== this.props.blueprint
      || prevProps.factsheetBlueprint !== this.props.factsheetBlueprint
      || prevProps.factInstanceKey !== this.props.factInstanceKey) {
      this.updateState()
    }
  }

  updateState () {
    const factDefId = this.props.parentBlueprint.id
    const fact = this.props.factsheetBlueprint.factTree.facts[factDefId]
    const attributeDefId = this.props.blueprint.id
    const attribute = this.props.factsheetBlueprint.factTree.attributes[attributeDefId]
    const required = getAttributeRequired(this.props.blueprint, attribute, fact)
    const value = this.props.data?.value ?? ''
    let startDate = null
    let endDate = null
    let dates = value.split(',')
    if (dates.length === 2) {
      startDate = dates[0] === '-' ? null : dayjs(dates[0], this.dateFormat, true)
      endDate = dates[1] === '-' ? null : dayjs(dates[1], this.dateFormat, true)
    }
    let valid
    const isRequiredAttribute = !!required.attribute
    if (isRequiredAttribute) {
      if (startDate === null && endDate === null) {
        valid = false
      } else {
        valid = (
          (startDate === null ? true : startDate.isValid())
          && (endDate === null ? true : endDate.isValid())
        )
      }
    } else {
      valid = (
        (startDate === null ? true : startDate.isValid())
        && (endDate === null ? true : endDate.isValid())
      )
    }

    // Fix dates, if in wrong order
    if (endDate !== null && endDate.isValid() && startDate !== null && startDate.isValid()) {
      if (endDate < startDate) {
        const tempStartDate = startDate
        startDate = endDate
        endDate = tempStartDate
        this.props.putAttribute(
          this.props.parentBlueprint.id,
          this.props.factInstanceKey,
          this.props.blueprint.id,
          {
            attributeDefId: this.props.blueprint.id,
            value: this.dateToString(startDate) + ',' + this.dateToString(endDate)
          },
          // auto date-order correction, not a user set -> skip the contradiction check.
          { skipContradictionCheck: true })
      }
    }

    if (startDate !== null) {
      startDate = startDate.isValid() ? startDate.toDate() : null
    }
    if (endDate !== null) {
      endDate = endDate.isValid() ? endDate.toDate() : null
    }

    this.setState(
      {
        valid,
        required: isRequiredAttribute,
        startDate: startDate,
        endDate: endDate
      }
    )
    this.props.putValidationStatus(factDefId, this.props.factInstanceKey, attributeDefId, valid)
  }

  onStartDateChange = (selectedStartDate) => {
    activityTrackingService.trackEvent(trackingEvent.editValue, {
      name: getTrackingFactLabel(this.props.parentBlueprint, this.props.factsheetBlueprint)
    })
    if (this.state.endDate === null && selectedStartDate === null) {
      this.props.deleteAttribute(
        this.props.parentBlueprint.id,
        this.props.factInstanceKey,
        this.props.blueprint.id
      )
      return
    }
    const dateString = this.dateToString(selectedStartDate) + ',' + this.dateToString(this.state.endDate)
    this.props.putAttribute(
      this.props.parentBlueprint.id,
      this.props.factInstanceKey,
      this.props.blueprint.id,
      {
        attributeDefId: this.props.blueprint.id,
        value: dateString
      })
  }

  onEndDateChange = (selectedEndDate) => {
    activityTrackingService.trackEvent(trackingEvent.editValue, {
      name: getTrackingFactLabel(this.props.parentBlueprint, this.props.factsheetBlueprint)
    })
    if (this.state.startDate === null && selectedEndDate === null) {
      this.props.deleteAttribute(
        this.props.parentBlueprint.id,
        this.props.factInstanceKey,
        this.props.blueprint.id
      )
      return
    }
    const dateString = this.dateToString(this.state.startDate) + ',' + this.dateToString(selectedEndDate)
    this.props.putAttribute(
      this.props.parentBlueprint.id,
      this.props.factInstanceKey,
      this.props.blueprint.id,
      {
        attributeDefId: this.props.blueprint.id,
        value: dateString
      })
  }

  dateToString (date, format = this.dateFormat) {
    if (date === null) {
      return '-'
    }
    return dayjs(date).format(format)
  }

  render () {
    const attributeDefId = this.props.blueprint.id
    const attribute = this.props.factsheetBlueprint.factTree.attributes[attributeDefId]
    const label = getComponentLabel(this.props.blueprint, attribute)
    const definition = getComponentDefinition(this.props.blueprint, attribute)
    const { t } = this.props
    if (this.props.collapsed) {
      if (this.state.valid && (this.state.startDate !== null || this.state.endDate !== null)) {
        return <div className={'attribute-daterange-select-collapsed'}>
          {label}:
          {this.state.startDate !== null && (
            <span> {t('DATE_FROM')} {dayjs(this.state.startDate).locale(this.props.i18n.language).format('L')}</span>
          )}
          {this.state.endDate !== null && (
            <span> {t('DATE_TO')} {dayjs(this.state.endDate).locale(this.props.i18n.language).format('L')}</span>
          )}
        </div>
      }
      return null
    }
    return (
      <div className={'attribute-daterange-select'}>
        <div className={'attribute-daterange-select-header'}>
          <FactsheetComponentLabel label={label} required={this.state.required} definition={definition}/>
        </div>
        <div className={'attribute-daterange-select-picker-container'}>
          <div>
            <label>{t('DATE_FROM')}</label>
            <DatePicker onChange={this.onStartDateChange} valid={this.state.valid}
                        selected={this.state.startDate}
                        maxDate={
                          this.state.endDate === null ? undefined : this.state.endDate
                        }
            />
          </div>
          <div>
            <label>{t('DATE_TO')}</label>
            <DatePicker onChange={this.onEndDateChange} valid={this.state.valid}
                        selected={this.state.endDate}
                        minDate={
                          this.state.startDate === null ? undefined : this.state.startDate
                        }/>
          </div>
        </div>
      </div>
    )
  }
}

AttributeDateRangeSelect.propTypes = {
  blueprint: PropTypes.shape(SHAPE_COMPONENT).isRequired,
  parentBlueprint: PropTypes.shape(SHAPE_COMPONENT).isRequired,
  factsheetBlueprint: PropTypes.shape(SHAPE_FACTSHEET_BLUEPRINT).isRequired,
  factInstanceKey: PropTypes.number.isRequired,
  data: PropTypes.object,
  collapsed: PropTypes.bool.isRequired,
  putAttribute: PropTypes.func.isRequired,
  deleteAttribute: PropTypes.func.isRequired,
  putValidationStatus: PropTypes.func.isRequired
}
export default withTranslation()(AttributeDateRangeSelect)
